import React, { useEffect, useState } from 'react';
import { authService, UserProfile } from '../../services';
import { useAuthStore } from '../../stores/authStore';
import { Button } from '../../components/common/Button';

const ProfilePage: React.FC = () => {
    const { user, setUser } = useAuthStore();
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        national_id: '',
        business_name: '',
        address: '',
        city: '',
        province: '',
        postal_code: '',
    });
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        try {
            const data = await authService.getProfile();
            setProfile(data);
            setFormData({
                full_name: data.full_name || '',
                email: data.email || '',
                national_id: data.national_id || '',
                business_name: data.business_name || '',
                address: data.address || '',
                city: data.city || '',
                province: data.province || '',
                postal_code: data.postal_code || '',
            });
        } catch (err) {
            console.error('Error loading profile:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            const updated = await authService.updateProfile(formData);
            setProfile(updated);
            setUser({ ...user!, full_name: updated.full_name });
            setIsEditing(false);
            setMessage({ type: 'success', text: 'اطلاعات با موفقیت ذخیره شد' });
        } catch (err: any) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'خطا در ذخیره اطلاعات' });
        } finally {
            setSaving(false);
        }
    };

    const getRoleLabel = (role?: string) => {
        const roles: Record<string, { label: string; description: string; icon: string }> = {
            customer: { label: 'مشتری عادی', description: 'دسترسی به قیمت‌های خرید تک', icon: '👤' },
            wholesale: { label: 'عمده‌فروش', description: 'تخفیف ۳۰٪ در اجرت ساخت', icon: '🏭' },
            retailer: { label: 'خرده‌فروش', description: 'تخفیف ۲۰٪ در اجرت ساخت', icon: '🏪' },
            goldsmith: { label: 'طلاساز', description: 'تخفیف ۴۰٪ در اجرت ساخت', icon: '⚒️' },
            admin: { label: 'مدیر', description: 'دسترسی کامل', icon: '👑' },
        };
        return roles[role || 'customer'] || roles.customer;
    };

    const roleInfo = getRoleLabel(profile?.role);

    if (loading) {
        return (
            <div className="p-4 lg:p-8">
                <div className="max-w-3xl mx-auto">
                    <div className="skeleton h-8 w-48 mb-8" />
                    <div className="card">
                        <div className="space-y-6">
                            {[...Array(6)].map((_, i) => (
                                <div key={i}>
                                    <div className="skeleton h-4 w-24 mb-2" />
                                    <div className="skeleton h-12 w-full" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-8">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-1">پروفایل</h1>
                        <p className="text-gray-500">مدیریت اطلاعات حساب کاربری</p>
                    </div>
                    {!isEditing && (
                        <Button
                            onClick={() => setIsEditing(true)}
                            className="btn-primary flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            ویرایش
                        </Button>
                    )}
                </div>

                {/* Messages */}
                {message.text && (
                    <div
                        className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${message.type === 'success'
                                ? 'bg-green-50 text-green-700 border border-green-200'
                                : 'bg-red-50 text-red-700 border border-red-200'
                            }`}
                    >
                        {message.type === 'success' ? (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        )}
                        <p className="font-medium">{message.text}</p>
                    </div>
                )}

                {/* Role Card */}
                <div className="card mb-6 bg-gradient-to-l from-[--color-primary] to-[--color-primary-light]">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center text-3xl">
                            {roleInfo.icon}
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-white mb-1">{roleInfo.label}</h3>
                            <p className="text-white/70 text-sm">{roleInfo.description}</p>
                        </div>
                        {profile?.discount_percent && profile.discount_percent > 0 && (
                            <div className="px-4 py-2 bg-accent/20 rounded-xl">
                                <p className="text-xs text-white/60 mb-1">تخفیف شما</p>
                                <p className="text-xl font-bold text-accent">{profile.discount_percent}%</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Profile Form */}
                <div className="card">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Phone (readonly) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                شماره موبایل
                            </label>
                            <input
                                type="text"
                                value={profile?.phone_number || ''}
                                disabled
                                className="input-field bg-gray-50"
                                dir="ltr"
                            />
                        </div>

                        {/* Full Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                نام و نام خانوادگی
                            </label>
                            <input
                                type="text"
                                value={formData.full_name}
                                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                disabled={!isEditing}
                                className="input-field"
                                placeholder="نام کامل خود را وارد کنید"
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                ایمیل
                            </label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                disabled={!isEditing}
                                className="input-field"
                                placeholder="email@example.com"
                                dir="ltr"
                            />
                        </div>

                        {/* National ID */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                کد ملی
                            </label>
                            <input
                                type="text"
                                value={formData.national_id}
                                onChange={(e) => setFormData({ ...formData, national_id: e.target.value })}
                                disabled={!isEditing}
                                className="input-field"
                                placeholder="کد ملی ۱۰ رقمی"
                                maxLength={10}
                                dir="ltr"
                            />
                        </div>

                        {/* Business Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                نام کسب و کار
                            </label>
                            <input
                                type="text"
                                value={formData.business_name}
                                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                                disabled={!isEditing}
                                className="input-field"
                                placeholder="نام فروشگاه یا کسب و کار"
                            />
                        </div>

                        {/* Address */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                آدرس
                            </label>
                            <textarea
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                disabled={!isEditing}
                                className="input-field min-h-[100px] resize-none"
                                placeholder="آدرس کامل"
                            />
                        </div>

                        {/* City & Province */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    استان
                                </label>
                                <input
                                    type="text"
                                    value={formData.province}
                                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                                    disabled={!isEditing}
                                    className="input-field"
                                    placeholder="استان"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    شهر
                                </label>
                                <input
                                    type="text"
                                    value={formData.city}
                                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                    disabled={!isEditing}
                                    className="input-field"
                                    placeholder="شهر"
                                />
                            </div>
                        </div>

                        {/* Postal Code */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                کد پستی
                            </label>
                            <input
                                type="text"
                                value={formData.postal_code}
                                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                                disabled={!isEditing}
                                className="input-field"
                                placeholder="کد پستی ۱۰ رقمی"
                                maxLength={10}
                                dir="ltr"
                            />
                        </div>

                        {/* Actions */}
                        {isEditing && (
                            <div className="flex gap-3 pt-4">
                                <Button
                                    type="submit"
                                    isLoading={saving}
                                    className="btn-gradient flex-1"
                                >
                                    ذخیره تغییرات
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setIsEditing(false);
                                        loadProfile();
                                    }}
                                    className="flex-1"
                                >
                                    انصراف
                                </Button>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
